import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import {
  TypedFormArray,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  ServiceProviderHandler,
  TreatmentPackage,
} from '@principle-theorem/principle-core';
import {
  IPackageOverrideWithQuantity,
  type IPricedServiceCodeEntry,
  type IServiceCode,
  type ITreatmentPackagePriceOverride,
} from '@principle-theorem/principle-core/interfaces';
import { combineLatest, ReplaySubject } from 'rxjs';

@Component({
  selector: 'pr-edit-treatment-package-service-items',
  templateUrl: './edit-treatment-package-service-items.component.html',
  styleUrls: ['./edit-treatment-package-service-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditTreatmentPackageServiceItemsComponent {
  serviceCodes$ = new ReplaySubject<IPricedServiceCodeEntry[]>(1);
  priceOverrides$ = new ReplaySubject<ITreatmentPackagePriceOverride[]>(1);
  @Output() updateServiceItems = new EventEmitter<
    ITreatmentPackagePriceOverride[]
  >();
  serviceCodesCtrl = new TypedFormArray<IPackageOverrideWithQuantity>([]);

  @Input()
  set serviceCodes(serviceCodes: IPricedServiceCodeEntry[]) {
    if (serviceCodes) {
      this.serviceCodes$.next(serviceCodes);
    }
  }

  @Input()
  set priceOverrides(priceOverrides: ITreatmentPackagePriceOverride[]) {
    if (priceOverrides) {
      this.priceOverrides$.next(priceOverrides);
    }
  }

  get overrideControls(): TypedFormGroup<IPackageOverrideWithQuantity>[] {
    return this.serviceCodesCtrl
      .controls as TypedFormGroup<IPackageOverrideWithQuantity>[];
  }

  constructor() {
    combineLatest([this.serviceCodes$, this.priceOverrides$])
      .pipe(takeUntilDestroyed())
      .subscribe(([serviceCodes, priceOverrides]) => {
        this.serviceCodesCtrl.clear({ emitEvent: false });
        TreatmentPackage.getMappedPriceOverrides(
          serviceCodes,
          priceOverrides
        ).forEach((serviceCode) => {
          this.serviceCodesCtrl.push(
            new TypedFormGroup<IPackageOverrideWithQuantity>({
              code: new TypedFormControl(serviceCode.code),
              type: new TypedFormControl(serviceCode.type),
              price: new TypedFormControl(serviceCode.price, {
                validators: Validators.required,
                updateOn: 'blur',
              }),
              quantity: new TypedFormControl(serviceCode.quantity),
            }),
            {
              emitEvent: false,
            }
          );
        });
      });

    this.serviceCodesCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.updateServiceItems.emit(value);
      });
  }

  resolveServiceCode(
    scopedCode: ITreatmentPackagePriceOverride
  ): IServiceCode | undefined {
    return ServiceProviderHandler.resolveServiceCode(
      scopedCode.type,
      scopedCode.code
    );
  }
}
